/** 主从组表单字段映射、runMode 切换、卷挂载同步（模型/应用实例共用） */
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep, get, set } from 'lodash';

import type {
  MaasGroupSpec,
  MaasGroupedWorkloadSpec,
  MaasResource,
  MaasResourceNetwork,
  MaasRunMode,
  MaasSharedFormValues,
  MaasSystemDiskSizes,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasRunMode as RunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  SYSTEM_VOLUME_NAME,
  VOLUME_SOURCE_EPHEMERAL,
  findDataVolumeInVolumes,
  getSystemVolumeSizeGi,
  getVolumeClaimName,
  isCreateDataVolume,
  isExistingDataVolume,
  normalizeDataVolumeForSubmit,
  normalizeSystemVolumeForSubmit,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { DEFAULT_SYSTEM_DISK_SIZE_GI, WORKER_NODES_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import {
  parseNodesField,
  readEmbeddedNodeSystemDiskGi,
  writeNodesField,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';

/** UI 辅助：判断数据盘为创建模板或已有 PVC（非 API 字段） */
export enum MaasDataDiskMode {
  Create = 'create',
  Existing = 'existing',
}

/** 表单主组字段：主从模式推理/存储/网络写在 headGroupSpec，单机与分布式写在 workerGroupSpecs[0] */
export function getPrimaryGroupField(runMode?: MaasRunMode): string {
  return runMode === RunMode.PrimarySecondary ? 'headGroupSpec' : 'workerGroupSpecs[0]';
}

/** 从表单读取组配置（合并 dot-path 子字段，避免整组 setFieldValue 丢失镜像等字段） */
export function readGroupFromForm(form: FormInstance, groupField: string): MaasGroupSpec | undefined {
  const group = form.getFieldValue(groupField) as MaasGroupSpec | undefined;
  const system = form.getFieldValue(`${groupField}.system`) as Record<string, unknown> | undefined;
  const resource = form.getFieldValue(`${groupField}.resource`) as MaasGroupSpec['resource'] | undefined;
  if (!group && !system && !resource) return undefined;
  return {
    ...(group || {}),
    ...(system ? { system: cloneDeep(system) } : {}),
    ...(resource ? { resource: cloneDeep(resource) } : {}),
  } as MaasGroupSpec;
}

/** 从表单读取主组（合并 dot-path 写入的 system / resource 子字段） */
export function getPrimaryGroupFromForm(form: FormInstance, runMode?: MaasRunMode): MaasGroupSpec | undefined {
  const mode = runMode ?? (form.getFieldValue('runMode') as MaasRunMode | undefined);
  const field = getPrimaryGroupField(mode);
  const merged = readGroupFromForm(form, field);
  if (!merged) return undefined;
  return {
    ...(merged.name ? merged : { name: mode === RunMode.PrimarySecondary ? 'master' : 'worker', replicas: 1 }),
    ...(merged.system ? { system: merged.system } : {}),
    ...(merged.resource ? { resource: merged.resource } : {}),
  };
}

export function primaryGroupField(form: FormInstance): string {
  return getPrimaryGroupField(form.getFieldValue('runMode') as MaasRunMode | undefined);
}

export {
  MODEL_DATA_VOLUME_NAME,
  getVolumeClaimName,
  getVolumeMountPath,
  getVolumePvcTemplate,
  isCreateDataVolume,
  isCreateDataVolumeConfigured,
  isExistingDataVolume,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';

export const DEFAULT_SYSTEM_VOLUME = {
  name: SYSTEM_VOLUME_NAME,
  type: 'System',
  reclaimPolicy: 'Delete',
  size: '10Gi',
  source: {
    type: VOLUME_SOURCE_EPHEMERAL,
    mountPath: '/',
  },
};

export function getDataDiskMode(volume?: Record<string, unknown>): MaasDataDiskMode | undefined {
  if (isExistingDataVolume(volume)) return MaasDataDiskMode.Existing;
  if (isCreateDataVolume(volume)) return MaasDataDiskMode.Create;
  return undefined;
}

export function findGroupVolume(volumes: Record<string, unknown>[] | undefined, type: 'System' | 'Data') {
  if (type === 'Data') {
    return findDataVolumeInVolumes(volumes);
  }
  return (volumes || []).find((v) => v.type === 'System');
}

export function getGroupSystemDiskSizeGi(group?: MaasGroupSpec): number {
  const volumes = get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  const systemVolume = findGroupVolume(volumes, 'System');
  if (!systemVolume) return 10;
  return getSystemVolumeSizeGi(systemVolume) || 10;
}

/** 读取组内系统盘 Gi；无 System 卷或无有效容量时返回 undefined（不默认 10） */
export function readGroupSystemDiskSizeGi(group?: MaasGroupSpec): number | undefined {
  const volumes = get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  const systemVolume = findGroupVolume(volumes, 'System');
  if (!systemVolume) return undefined;
  return getSystemVolumeSizeGi(systemVolume);
}

export function getGroupMinSystemDiskSizeGi(group?: MaasGroupSpec, fallbackGroup?: MaasGroupSpec): number {
  const minFromSystem = get(group, 'system.minSystemDiskGi');
  const parsed = Number(minFromSystem);
  if (!Number.isNaN(parsed) && parsed >= 10) return parsed;
  if (fallbackGroup) {
    const fallbackMin = get(fallbackGroup, 'system.minSystemDiskGi');
    const fallbackParsed = Number(fallbackMin);
    if (!Number.isNaN(fallbackParsed) && fallbackParsed >= 10) return fallbackParsed;
  }
  return Math.max(10, getGroupSystemDiskSizeGi(group));
}

/** 读取 Worker 共用系统盘大小（以第一个 Worker 为准） */
export function getWorkersSystemDiskSizeGi(workers?: MaasGroupSpec[]): number {
  return getGroupSystemDiskSizeGi(workers?.[0]);
}

/** 读取 Worker 共用系统盘最小值 */
export function getWorkersMinSystemDiskSizeGi(workers?: MaasGroupSpec[], fallbackGroup?: MaasGroupSpec): number {
  return getGroupMinSystemDiskSizeGi(workers?.[0], fallbackGroup);
}

/** 读取 Worker 节点列表（优先 workerNodes JSON，兼容遗留 workerGroupSpecs 字段） */
export function getWorkerGroupListSpecs(form: FormInstance): MaasGroupSpec[] {
  const runMode = form.getFieldValue('runMode') as MaasRunMode | undefined;
  const fromNodes = parseNodesField(form.getFieldValue(WORKER_NODES_FIELD));
  if (fromNodes.length) {
    return runMode === RunMode.Single ? fromNodes.slice(0, 1) : fromNodes;
  }
  if (runMode === RunMode.Single) {
    const worker = readGroupFromForm(form, 'workerGroupSpecs[0]');
    return worker ? [worker] : [];
  }
  const workers = form.getFieldValue('workerGroupSpecs');
  return Array.isArray(workers) ? workers : [];
}

/** 写入 Worker 节点列表到 workerNodes（不含 Data 卷） */
export function setWorkerGroupListSpecs(form: FormInstance, workers: MaasGroupSpec[]) {
  const next = cloneDeep(workers).map((worker) => stripStorageFromGroupSpec(worker)!);
  writeNodesField(form, WORKER_NODES_FIELD, next);
}

/** UI 侧主组 Data 卷（与节点组 resource.storage 分离，提交时再组装；表单存 JSON 字符串） */
export const PRIMARY_DATA_VOLUME_FIELD = '_primaryDataVolume';

/** 解析表单中的 Data 卷 JSON（兼容历史对象直存） */
export function parsePrimaryDataVolumeField(raw: unknown): Record<string, unknown> | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return cloneDeep(raw as Record<string, unknown>);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return cloneDeep(parsed as Record<string, unknown>);
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** 写入 Data 卷 JSON 字段 */
export function writePrimaryDataVolumeField(form: FormInstance, volume?: Record<string, unknown>) {
  if (!volume) {
    form.setFieldValue(PRIMARY_DATA_VOLUME_FIELD, undefined);
    return;
  }
  form.setFieldValue(PRIMARY_DATA_VOLUME_FIELD, JSON.stringify(cloneDeep(volume)));
}

/** 从组 spec 中移除 resource.storage（表单阶段不持久化卷配置） */
export function stripStorageFromGroupSpec(group?: MaasGroupSpec): MaasGroupSpec | undefined {
  if (!group) return group;
  const next = cloneDeep(group);
  if (!next.resource) return next;
  const resource = { ...(next.resource as Record<string, unknown>) };
  delete resource.storage;
  if (Object.keys(resource).length) {
    next.resource = resource as MaasGroupSpec['resource'];
  } else {
    delete next.resource;
  }
  return next;
}

/** 从节点组 spec 提取系统盘大小（详情转表单用；无卷时不写默认 10） */
export function extractSystemDiskSizesFromGroups(
  values: Pick<MaasSharedFormValues, 'runMode' | 'headGroupSpec' | 'workerGroupSpecs'>,
): MaasSystemDiskSizes {
  const sizes: MaasSystemDiskSizes = {};
  if (values.headGroupSpec) {
    const headGi = readGroupSystemDiskSizeGi(values.headGroupSpec);
    if (headGi != null && headGi > 0) sizes.headGroupSpec = headGi;
  }
  const workers = values.workerGroupSpecs ?? [];
  if (workers.length) {
    const workerGi = readGroupSystemDiskSizeGi(workers[0]);
    if (workerGi != null && workerGi > 0) {
      sizes.workers = workerGi;
      if (values.runMode === RunMode.Single) {
        sizes['workerGroupSpecs[0]'] = workerGi;
      }
    }
  }
  return sizes;
}

/** 从表单 UI 字段（master/worker.systemDiskSize）或节点 JSON _systemDiskSize 解析系统盘大小（Gi） */
export function resolveSystemDiskGiFromFormValues(
  values: Pick<MaasSharedFormValues, 'master.systemDiskSize' | 'worker.systemDiskSize'>,
  groupKey: keyof MaasSystemDiskSizes,
  fallbackGroup?: MaasGroupSpec,
): number | undefined {
  const embeddedGi = readEmbeddedNodeSystemDiskGi(fallbackGroup);
  if (embeddedGi != null) return embeddedGi;

  const masterDisk = values['master.systemDiskSize'];
  const workerDisk = values['worker.systemDiskSize'];
  if (groupKey === 'headGroupSpec') {
    if (typeof masterDisk === 'number' && masterDisk > 0) return masterDisk;
  } else if (typeof workerDisk === 'number' && workerDisk > 0) {
    return workerDisk;
  }
  return readGroupSystemDiskSizeGi(fallbackGroup);
}

/** 读取主组 Data 卷：优先独立字段，兼容旧表单嵌在组 spec 中的数据 */
export function resolvePrimaryDataVolumeFromValues(
  values: Pick<MaasSharedFormValues, 'runMode' | 'headGroupSpec' | 'workerGroupSpecs' | '_primaryDataVolume'>,
): Record<string, unknown> | undefined {
  const fromField = parsePrimaryDataVolumeField(values._primaryDataVolume);
  if (fromField) return fromField;
  const primary = getPrimaryGroupFromValues(values as MaasSharedFormValues, values.runMode);
  const volumes = get(primary, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  return findGroupVolume(volumes, 'Data');
}

/** 提交前将独立 Data 卷与系统盘大小组装回节点组 spec（未配置系统盘时不静默写入 10Gi） */
export function attachPrimaryDataVolumeForSubmit<T extends MaasSharedFormValues>(values: T): T {
  const next = cloneDeep(values);

  const attachToGroup = (group: MaasGroupSpec | undefined, groupKey: keyof MaasSystemDiskSizes): MaasGroupSpec | undefined => {
    if (!group) return group;
    const dataVolume = resolvePrimaryDataVolumeFromValues(values);
    const perNodeSystemGi = readGroupSystemDiskSizeGi(group);
    const stripped = stripStorageFromGroupSpec(group)!;
    const globalSystemDiskGi = get(next, `${groupKey === 'headGroupSpec' ? 'master' : 'worker'}.systemDiskSize`);
    const systemDiskGi = perNodeSystemGi ?? globalSystemDiskGi;
    const volumes: Record<string, unknown>[] = [];
    if (typeof systemDiskGi === 'number' && systemDiskGi > 0) {
      volumes.push(normalizeSystemVolumeForSubmit(cloneDeep(DEFAULT_SYSTEM_VOLUME), systemDiskGi));
    }
    if (dataVolume) {
      volumes.push(cloneDeep(dataVolume));
    }
    set(stripped, 'resource.storage.volume', volumes);
    return stripped;
  };

  const runMode = next.runMode;

  if (runMode === RunMode.PrimarySecondary && next.headGroupSpec) {
    next.headGroupSpec = attachToGroup(next.headGroupSpec, 'headGroupSpec');
  }

  const workers = getWorkerGroupListForSubmit(next);
  if (workers.length > 0) {
    next.workerGroupSpecs = workers.map((worker, index) => {
      return attachToGroup(worker, 'workerGroupSpecs[0]')!;
    });
  }

  return next;
}

/** 提交/校验时读取 Worker 列表（已组装 spec 或 workerNodes UI 字段） */
export function getWorkerGroupListForSubmit(values: MaasSharedFormValues): MaasGroupSpec[] {
  const runMode = values.runMode;
  if (values.workerGroupSpecs?.length) {
    if (runMode === RunMode.Single) {
      const primary = values.workerGroupSpecs[0];
      return primary ? [cloneDeep(primary)] : [];
    }
    return cloneDeep(values.workerGroupSpecs);
  }
  const fromNodes = parseNodesField(values.workerNodes);
  if (runMode === RunMode.Single) {
    return fromNodes.length ? [cloneDeep(fromNodes[0])] : [];
  }
  return cloneDeep(fromNodes);
}

/** 节点组 GPU 是否已配置数量（计价 / 直建共用） */
export function hasGroupGpuQuantity(resource?: Record<string, unknown>): boolean {
  const gpu = get(resource, 'gpu') as Record<string, unknown> | undefined;
  if (!gpu) return false;
  const qty = gpu.number ?? get(gpu, 'card.request') ?? get(gpu, 'resource.nvidia.com/gpu.request');
  if (qty == null || qty === '') return false;
  const n = Number(qty);
  return Number.isFinite(n) && n > 0;
}

/** 节点组弹窗可编辑字段：不含 resource.storage（数据盘/系统盘在其它步骤维护，提交时再组装） */
export function pickGroupNodeConfigForModal(group?: MaasGroupSpec, options?: { embeddedNodeConfig?: boolean }): MaasGroupSpec | undefined {
  if (!group) return undefined;
  const resource = (group.resource || {}) as Record<string, unknown>;
  const network = (resource.network || {}) as Record<string, unknown>;
  const nodeResource: Record<string, unknown> = {};
  if (resource.cpu) nodeResource.cpu = cloneDeep(resource.cpu);
  if (resource.memory) nodeResource.memory = cloneDeep(resource.memory);
  if (resource.gpu) nodeResource.gpu = cloneDeep(resource.gpu);
  if (resource.cpuPricing) nodeResource.cpuPricing = cloneDeep(resource.cpuPricing);
  if (resource.memoryPricing) nodeResource.memoryPricing = cloneDeep(resource.memoryPricing);
  if (resource.gpuPricing) nodeResource.gpuPricing = cloneDeep(resource.gpuPricing);
  const embeddedNetwork: Record<string, unknown> = {};
  if (network.egressRate !== undefined && network.egressRate !== null && network.egressRate !== '') {
    embeddedNetwork.egressRate = network.egressRate;
  }
  if (options?.embeddedNodeConfig) {
    if (network.service !== undefined) embeddedNetwork.service = cloneDeep(network.service);
    const nics = resource.nics ?? network.nics;
    if (nics !== undefined) nodeResource.nics = cloneDeep(nics);
  }
  if (Object.keys(embeddedNetwork).length) {
    nodeResource.network = embeddedNetwork;
  }
  const system = group.system ? (cloneDeep(group.system) as Record<string, unknown>) : {};
  if (group.registry && !system.registry) {
    system.registry = cloneDeep(group.registry);
  }
  const embedded = group as MaasGroupSpec & {
    _containerDeployHealthCheck?: unknown;
    _containerDeploySharedMemory?: unknown;
    _healthCheckEnabled?: boolean;
    _sharedMemoryEnabled?: boolean;
    _nicEnabled?: boolean;
    _systemDiskSize?: number;
    _systemDiskPricing?: Record<string, unknown>;
    maasInferenceSource?: string;
    inferenceFrameworkImage?: string;
  };
  return {
    name: group.name,
    replicas: group.replicas,
    ...(group.initCommand ? { initCommand: cloneDeep(group.initCommand) } : {}),
    ...(Object.keys(system).length ? { system } : {}),
    ...(Object.keys(nodeResource).length ? { resource: nodeResource } : {}),
    ...(options?.embeddedNodeConfig && embedded._containerDeployHealthCheck !== undefined
      ? { _containerDeployHealthCheck: cloneDeep(embedded._containerDeployHealthCheck) }
      : {}),
    ...(options?.embeddedNodeConfig && embedded._containerDeploySharedMemory !== undefined
      ? { _containerDeploySharedMemory: cloneDeep(embedded._containerDeploySharedMemory) }
      : {}),
    ...(options?.embeddedNodeConfig && embedded._healthCheckEnabled !== undefined
      ? { _healthCheckEnabled: embedded._healthCheckEnabled }
      : {}),
    ...(options?.embeddedNodeConfig && embedded._sharedMemoryEnabled !== undefined
      ? { _sharedMemoryEnabled: embedded._sharedMemoryEnabled }
      : {}),
    ...(options?.embeddedNodeConfig && embedded._nicEnabled !== undefined ? { _nicEnabled: embedded._nicEnabled } : {}),
    ...(options?.embeddedNodeConfig && embedded._systemDiskSize !== undefined ? { _systemDiskSize: embedded._systemDiskSize } : {}),
    ...(options?.embeddedNodeConfig && embedded._systemDiskPricing !== undefined
      ? { _systemDiskPricing: cloneDeep(embedded._systemDiskPricing) }
      : {}),
    ...(options?.embeddedNodeConfig && embedded.maasInferenceSource !== undefined
      ? { maasInferenceSource: embedded.maasInferenceSource }
      : {}),
    ...(options?.embeddedNodeConfig && embedded.inferenceFrameworkImage !== undefined
      ? { inferenceFrameworkImage: embedded.inferenceFrameworkImage }
      : {}),
  };
}

/** 确保组基础字段存在（仅写 dot-path，避免覆盖 system 等嵌套字段） */
function ensureGroupBaseFields(form: FormInstance, groupField: string, defaultName: string) {
  if (form.getFieldValue(`${groupField}.name`) === undefined) {
    form.setFieldValue(`${groupField}.name`, defaultName);
  }
  if (form.getFieldValue(`${groupField}.replicas`) === undefined) {
    form.setFieldValue(`${groupField}.replicas`, 1);
  }
  if (form.getFieldValue(`${groupField}.resource.network`) === undefined) {
    form.setFieldValue(`${groupField}.resource.network`, {});
  }
}

/** 仅更新组内 storage.volume，不覆盖 system / resource 等其它 dot-path 字段 */
function setGroupStorageVolumes(form: FormInstance, groupField: string, volumes: Record<string, unknown>[]) {
  form.setFieldValue(`${groupField}.resource.storage.volume`, volumes);
}

/** 为指定组字段初始化基础字段（不含 resource.storage，提交时再组装） */
export function ensureGroupSpec(form: FormInstance, groupField: string, defaultName = 'master', _isPrimaryGroup = false) {
  ensureGroupBaseFields(form, groupField, defaultName);
  const group = readGroupFromForm(form, groupField);
  if (group) {
    const stripped = stripStorageFromGroupSpec(group);
    if (stripped) {
      form.setFieldValue(groupField, stripped);
    }
  }
  return readGroupFromForm(form, groupField);
}

/** 初始化主组骨架（网络/存储/系统盘与 CRD 结构一致；数据盘由用户显式添加） */
export function ensurePrimaryGroupSpec(form: FormInstance, _mountPath = '/models') {
  const runMode = form.getFieldValue('runMode') as MaasRunMode | undefined;

  if (runMode === RunMode.PrimarySecondary) {
    return ensureGroupSpec(form, 'headGroupSpec', 'master', true);
  }

  form.setFieldValue('headGroupSpec', undefined);

  // 仅单机模式自动向 workerGroupSpecs 数组写入默认 Worker
  if (runMode === RunMode.Single) {
    const workers = getWorkerGroupListSpecs(form);
    if (!workers.length) {
      ensureGroupBaseFields(form, 'workerGroupSpecs[0]', 'worker');
    }
    return ensureGroupSpec(form, 'workerGroupSpecs[0]', 'worker', true);
  }

  // 分布式 Worker 列表为空时不写入 workerGroupSpecs[0]，避免节点列表出现幽灵卡片
  const workers = getWorkerGroupListSpecs(form);
  if (!workers.length) {
    return undefined;
  }

  return ensureGroupSpec(form, 'workerGroupSpecs[0]', 'worker', true);
}

export function syncPrimaryDataVolumeMountPath(form: FormInstance, mountPath: string) {
  const dataVolume = getPrimaryDataVolume(form);
  if (!dataVolume) return;
  const next = cloneDeep(dataVolume);
  set(next, 'source.mountPath', mountPath);
  writePrimaryDataVolumeField(form, next);
}

export function finalizeGroupSpecForSubmit(group: MaasGroupSpec, mountPath?: string, options?: { dataVolumeName?: string }): MaasGroupSpec {
  const next = cloneDeep(group);
  const volumes = cloneDeep(get(next, 'resource.storage.volume') as Record<string, unknown>[]) || [];
  const mp = mountPath || '/models';
  const dataVolume = findGroupVolume(volumes, 'Data');
  const normalizedData = dataVolume ? normalizeDataVolumeForSubmit(dataVolume, mp, { volumeName: options?.dataVolumeName }) : undefined;
  const systemVolume = findGroupVolume(volumes, 'System');
  const diskGi = systemVolume ? getSystemVolumeSizeGi(systemVolume) : undefined;
  const rest = volumes.filter((v) => v.type !== 'System' && v !== dataVolume);
  const ordered = [
    ...(systemVolume && diskGi != null && diskGi > 0 ? [normalizeSystemVolumeForSubmit(systemVolume, diskGi)] : []),
    ...(normalizedData ? [normalizedData] : []),
    ...rest,
  ];
  set(next, 'resource.storage.volume', ordered);
  return next;
}

export function getPrimaryGroupFromValues(
  values: MaasGroupedWorkloadSpec | MaasSharedFormValues,
  runMode?: MaasRunMode,
): MaasGroupSpec | undefined {
  const mode = runMode ?? values.runMode;
  const shared = values as MaasSharedFormValues;

  if (mode === RunMode.PrimarySecondary) {
    if (values.headGroupSpec) return values.headGroupSpec;
    return parseNodesField(shared.masterNodes)[0];
  }

  const workers = getWorkerGroupListForSubmit(shared);
  if (workers.length > 0) return workers[0];

  return values.workerGroupSpecs?.[0];
}

export function getPrimaryDataVolumeClaimName(
  values: Pick<MaasSharedFormValues, 'runMode' | 'headGroupSpec' | 'workerGroupSpecs' | '_primaryDataVolume'>,
): string | undefined {
  const data = resolvePrimaryDataVolumeFromValues(values);
  return getVolumeClaimName(data);
}

export function getPrimaryDataVolume(form: FormInstance): Record<string, unknown> | undefined {
  const fromField = parsePrimaryDataVolumeField(form.getFieldValue(PRIMARY_DATA_VOLUME_FIELD));
  if (fromField) return fromField;
  const field = primaryGroupField(form);
  const group = form.getFieldValue(field) as MaasGroupSpec | undefined;
  const volumes = get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  return findGroupVolume(volumes, 'Data');
}

export function setPrimaryDataVolume(form: FormInstance, dataVolume: Record<string, unknown> | undefined) {
  writePrimaryDataVolumeField(form, dataVolume);
  // Data 卷独立存储；仅清理组 spec 内嵌的旧 Data 卷，不写整组对象以免覆盖镜像 dot-path 字段
  const field = primaryGroupField(form);
  const group = readGroupFromForm(form, field);
  const volumes = get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined;
  if (!volumes?.some((volume) => volume.type === 'Data')) return;
  const filtered = volumes.filter((volume) => volume.type !== 'Data');
  if (filtered.length) {
    setGroupStorageVolumes(form, field, filtered);
    return;
  }
  form.setFieldValue(`${field}.resource.storage.volume`, undefined);
}

export function clearPrimaryDataVolume(form: FormInstance) {
  setPrimaryDataVolume(form, undefined);
}
